import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  Stack,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { useComputeAdjustedDateRange } from '../_hooks/useComputeAdjustedDateRange';
import { formatCost } from '../_utils/formatCost';
import { ComputeSpendDatapoint, ComputeSpendSeries } from '../_types';

type Props = {
  data: ComputeSpendDatapoint[];
  series: ComputeSpendSeries[];
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  refetch?: () => void;
};

export const ComputeSpendLineChart = ({
  data,
  series,
  isLoading,
  isFetching,
  error,
  refetch,
}: Props) => {
  const { timeZone } = useComputeAdjustedDateRange();

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  return (
    <Stack flex={1} minHeight={200}>
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load compute spend"
          refresh={refetch}
          compact
        />
      ) : (
        <ChartContainer<ComputeSpendDatapoint>
          data={data}
          isLoading={isCardLoading}
          sx={{ width: '100%', height: '100%' }}
          margin={{ top: 8, bottom: 24, left: 0, right: 0 }}
          testId="compute-spend-chart"
        >
          <ChartGrid hideVertical />
          {series.map((s) => (
            <Line<ComputeSpendDatapoint>
              key={s.id}
              dataKey={(d) => d.values[s.id] ?? 0}
              stroke={s.color}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
              tooltipConfig={{ displayName: s.name }}
            />
          ))}
          <YAxis
            tickCount={5}
            tickFormatter={(value: string | number) => `${value}`}
            padding={{ top: 30 }}
            allowDecimals
          />
          <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
          <ChartTooltip<ComputeSpendDatapoint>
            testId="data-popper"
            header={(headerProps) => (
              <ChartTooltipTimestampHeader
                {...headerProps}
                timezone={timeZone}
              />
            )}
            formatValue={(value) => formatCost(Number(value))}
          />
        </ChartContainer>
      )}
    </Stack>
  );
};
